import { useCallback, useEffect, useState } from "react";

import { divisionApi, subsidiaryApi, toArray } from "../Services";

/**
 * Subsidiary/Division and Cost Centre as configured in Panel Contract.
 *
 * These used to be read from `subsidiaryList()` / `getDivisionList()` in
 * Utils/Constant — the hardcoded ePOD org tree — so the Work Order screens
 * offered entries Panel Contract knows nothing about. Approval Management owns
 * this data, so the pickers now read the same two endpoints its tabs do.
 *
 * A subsidiary or cost centre that has not been configured is therefore not
 * offered at all: with nothing set up the lists come back empty, which is the
 * intended behaviour rather than a failure to load.
 */

/** One configured cost centre and the subsidiary it belongs to. */
interface CostCentre {
  subsidiary: string;
  division: string;
}

const clean = (value: any) => String(value ?? "").trim();
/* A–Z, case-insensitive: these back the Subsidiary/Division and Cost Centre
   dropdowns, which came out in whatever order the API returned them. */
const unique = (values: string[]) =>
  Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );

export const usePanelOrgTree = () => {
  const [subsidiaries, setSubsidiaries] = useState<string[]>([]);
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  /* subsidiaryId -> subsidiaryName. Several endpoints answer with the id where
     the UI shows the name — the approver list prints a bare uuid in its
     Subsidiary/Division column, and matching cost centres by name against
     those records finds nothing. */
  const [nameById, setNameById] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    /* The paged list routes, not the `all-*` ones: these are what the Approval
       Management tabs read, and those tabs demonstrably show the configured
       records with their names. `get-all-division` was returning nothing the
       filter could use, which is why Select Cost Centre said "No options
       available" while the Cost Center tab listed rows. */
    Promise.all([
      subsidiaryApi.list({ page: 0, size: 1000 }).catch(() => []),
      divisionApi.list({ page: 0, size: 1000 }).catch(() => []),
    ])
      .then(([subsidiaryRows, divisionRows]) => {
        if (cancelled) return;

        const lookup: Record<string, string> = {};
        toArray(subsidiaryRows).forEach((row: any) => {
          const id = clean(row.subsidiaryId);
          const name = clean(row.subsidiaryName);
          if (id && name) lookup[id] = name;
        });
        setNameById(lookup);
        setSubsidiaries(
          unique(toArray(subsidiaryRows).map((row: any) => clean(row.subsidiaryName)))
        );

        setCostCentres(
          toArray(divisionRows)
            .map((row: any) => {
              /* The owning subsidiary arrives as a name on some endpoints and
                 as an id on others; resolve an id so both end up as a name. */
              const owner = clean(row.subsidiary ?? row.subsidiaryName ?? row.subsidiaryId);
              return {
                subsidiary: lookup[owner] ?? owner,
                division: clean(row.division ?? row.divisionName),
              };
            })
            .filter((row: CostCentre) => row.division)
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Cost centres under the given subsidiaries, or every configured one when
   * none is supplied — the filter shows the full list until a subsidiary
   * narrows it, while the wizard always passes the single chosen subsidiary.
   */
  const costCentresFor = useCallback(
    (names: string[] = []) =>
      unique(
        (names.length
          ? costCentres.filter((row) => names.includes(row.subsidiary))
          : costCentres
        ).map((row) => row.division)
      ),
    [costCentres]
  );

  /**
   * A subsidiary as it should be shown. Given an id it returns the configured
   * name; given a name, or something unrecognised, it returns it untouched.
   */
  /* Both of these are memoised: callers put them in effect dependency lists,
     and a fresh function each render re-ran those effects on every render —
     the Edit Approver form re-hydrated from the server continuously, which
     overwrote each keystroke and left the fields looking read-only. */
  const resolveSubsidiary = useCallback(
    (value: any) => {
      const raw = clean(value);
      return nameById[raw] ?? raw;
    },
    [nameById]
  );

  return { subsidiaries, costCentresFor, resolveSubsidiary, isLoading };
};
